/**
 * Authentication Service for Hotel Portal
 * Handles login, logout, token management, and token refresh
 */

import { API_URL } from '../config/apiConfig';
import { apiErrorMessage } from '../utils/apiError';

export interface LoginCredentials {
  email: string;
  password: string;
  certificateData?: string; // Optional certificate for enhanced security
}

export interface LoginResponse {
  access_token: string;
  access_token_expires: string;
  refresh_token: string;
  refresh_token_expires: string;
  hotel_id: string;
  hotel_name: string;
  must_change_password?: boolean;
  token_type: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  access_token_expires: string;
  refresh_token: string;
  refresh_token_expires: string;
  token_type: string;
}

export interface AuthError {
  error: string;
  code: string;
}

/**
 * Login with email and password
 * Tries hotel entity login first, then falls back to hotel user (staff) login
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  // Try hotel entity login first (silent failure — fallback to user login)
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/hotel/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });

    if (response.ok) {
      const data: LoginResponse = await response.json();
      // Also check user-level login for must_change_password flag
      try {
        const userCheck = await fetch(`${API_URL}/api/v1/auth/hotel/user/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
        });
        if (userCheck.ok) {
          const userData = await userCheck.json();
          if (userData.must_change_password) {
            data.must_change_password = true;
          }
        }
      } catch { /* ignore — hotel login already succeeded */ }
      storeLoginData(data, credentials.email);
      return data;
    }
  } catch {
    // Network error on hotel entity login — try user login
  }

  // Fallback: hotel user (staff) login
  const userResponse = await fetch(`${API_URL}/api/v1/auth/hotel/user/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (!userResponse.ok) {
    const err = await userResponse.json().catch(() => ({ error: 'Login failed' }));
    const errorMsg = typeof err.error === 'string' ? err.error
      : err.error?.message || err.message || 'Login failed';
    throw new Error(errorMsg);
  }

  const data: LoginResponse = await userResponse.json();
  storeLoginData(data, credentials.email);
  return data;
};

/**
 * Store login response data in localStorage
 */
function storeLoginData(data: LoginResponse, credentialEmail: string): void {
  localStorage.setItem('token', data.access_token);
  localStorage.setItem('tokenExpiresAt', data.access_token_expires);
  localStorage.setItem('hotelId', data.hotel_id);
  localStorage.setItem('hotelName', data.hotel_name);

  let role = 'hotel_admin';
  let email = '';
  try {
    const payload = JSON.parse(atob(data.access_token.split('.')[1]));
    const backendRole = (payload.role || '').toString();
    const roleMap: Record<string, string> = {
      'hotel': 'hotel_admin',
      'ADMIN': 'hotel_admin',
      'MANAGER': 'hotel_manager',
      'RECEPTIONIST': 'receptionist',
      'STAFF': 'staff',
      'hotel_admin': 'hotel_admin',
      'hotel_manager': 'hotel_manager',
      'receptionist': 'receptionist',
      'staff': 'staff',
      'SUPER_ADMIN': 'super_admin',
      'super_admin': 'super_admin',
    };
    role = roleMap[backendRole] || 'hotel_admin';
    email = payload.email || payload.sub || credentialEmail;
  } catch {
    email = credentialEmail;
  }
  localStorage.setItem('user', JSON.stringify({
    id: data.hotel_id,
    email,
    name: data.hotel_name,
    role,
    hotelId: data.hotel_id,
    hotelName: data.hotel_name,
  }));

  if (data.must_change_password) {
    localStorage.setItem('mustChangePassword', 'true');
  } else {
    localStorage.removeItem('mustChangePassword');
  }
}

/**
 * Logout and clear stored credentials
 */
export const logout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('tokenExpiresAt');
  localStorage.removeItem('hotelId');
  localStorage.removeItem('hotelName');
  localStorage.removeItem('user');
  localStorage.removeItem('mustChangePassword');
};

/**
 * Get stored token
 */
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) {
    return false;
  }

  // Check if token is expired
  const expiresAt = localStorage.getItem('tokenExpiresAt');
  if (!expiresAt) {
    return false;
  }

  const expirationDate = new Date(expiresAt);
  const now = new Date();

  return expirationDate > now;
};

/**
 * Refresh the authentication token
 */
export const refreshToken = async (): Promise<string> => {
  const currentToken = getToken();
  
  if (!currentToken) {
    throw new Error('No token to refresh');
  }

  const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${currentToken}`,
    },
    body: JSON.stringify({ token: currentToken }),
  });

  if (!response.ok) {
    let errorMsg = 'Token refresh failed';
    try {
      const error = await response.json();
      errorMsg = error.error?.message || error.error || errorMsg;
    } catch {}
    throw new Error(errorMsg);
  }

  const data = await response.json();
  
  // API gateway returns { token, expires_at } while hotel-service returns { access_token, access_token_expires }
  const newToken = data.access_token || data.token;
  const newExpires = data.access_token_expires || data.expires_at;
  
  if (newToken) {
    localStorage.setItem('token', newToken);
  }
  if (newExpires) {
    localStorage.setItem('tokenExpiresAt', newExpires);
  }
  
  return newToken || currentToken;
};

/**
 * Get hotel information from storage
 */
export const getHotelInfo = (): { hotelId: string; hotelName: string } | null => {
  const hotelId = localStorage.getItem('hotelId');
  const hotelName = localStorage.getItem('hotelName');
  
  if (!hotelId || !hotelName) {
    return null;
  }
  
  return { hotelId, hotelName };
};

/** Best-effort hotel id for API payloads when JWT omits hotel_id claim. */
export function getStoredHotelId(): string {
  const direct = localStorage.getItem('hotelId');
  if (direct) return direct;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return (u.hotelId || u.hotel_id || '') as string;
  } catch {
    return '';
  }
}

/**
 * Check if token needs refresh (within 5 minutes of expiration)
 */
export const shouldRefreshToken = (): boolean => {
  const expiresAt = localStorage.getItem('tokenExpiresAt');
  if (!expiresAt) {
    return false;
  }

  const expirationDate = new Date(expiresAt);
  const now = new Date();
  const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

  return expirationDate <= fiveMinutesFromNow && expirationDate > now;
};

/**
 * Check if user must change password
 */
export const mustChangePassword = (): boolean => {
  return localStorage.getItem('mustChangePassword') === 'true';
};

/**
 * Clear must change password flag
 */
export const clearMustChangePassword = (): void => {
  localStorage.removeItem('mustChangePassword');
};

/**
 * Get current user information
 */
export const getCurrentUser = (): { email: string; hotelId: string; hotelName: string } | null => {
  const hotelInfo = getHotelInfo();
  if (!hotelInfo) {
    return null;
  }
  
  // For now, we don't store email separately, so return a placeholder
  return {
    email: '', // Email not stored in localStorage for security
    hotelId: hotelInfo.hotelId,
    hotelName: hotelInfo.hotelName,
  };
};

/**
 * Change password for authenticated user
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_URL}/api/v1/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      currentPassword,
      newPassword,
    }),
  });

  if (!response.ok) {
    const error: AuthError = await response.json();
    throw new Error(apiErrorMessage(error, 'Password change failed'));
  }
  
  // Clear must change password flag after successful change
  clearMustChangePassword();
};

/**
 * Force change password (for first-time login or password reset)
 */
export const forceChangePassword = async (newPassword: string): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  // Extract email from stored user or JWT for the backend binding
  let email = '';
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    email = user.email || '';
  } catch { /* ignore */ }
  if (!email) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      email = payload.email || payload.sub || '';
    } catch { /* ignore */ }
  }

  const response = await fetch(`${API_URL}/api/v1/auth/force-change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      email,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    const error: AuthError = await response.json();
    throw new Error(apiErrorMessage(error, 'Password change failed'));
  }
  
  // Clear must change password flag after successful change
  clearMustChangePassword();
};

export const authService = {
  login,
  logout,
  getToken,
  isAuthenticated,
  refreshToken,
  getHotelInfo,
  shouldRefreshToken,
  mustChangePassword,
  clearMustChangePassword,
  getCurrentUser,
  changePassword,
  forceChangePassword,
};

export default authService;
